import { ReactNode } from "react";

import { Button, Card, Loader, Notification, Toggle } from "@stellar/design-system";

import { ErrorWithExtras } from "@/components/ErrorWithExtras";

import { AppError } from "@/types";

type CardWithTogglerBaseProps = {
  toggleId: string;
  label: string | ReactNode;
  note: ReactNode;
  isPending?: boolean;
  error?: AppError | null;
  children?: ReactNode;
  isHighlighted?: boolean;
};

type ToggleVariantProps = {
  variant?: "toggle";
  isChecked: boolean;
  onToggleChange: () => void;
};

type ActionVariantProps = {
  variant: "action";
  actionLabel?: string;
  onAction?: () => void;
  actionButtonVariant?: "primary" | "secondary" | "tertiary";
};

type CardWithTogglerProps = CardWithTogglerBaseProps & (ToggleVariantProps | ActionVariantProps);

export const CardWithToggler = (props: CardWithTogglerProps) => {
  const { toggleId, label, note, isPending, error, children, isHighlighted } = props;

  const renderControl = () => {
    if (props.variant === "action") {
      const { actionLabel, onAction, actionButtonVariant = "tertiary" } = props;
      if (!actionLabel || !onAction) return null;
      return (
        <Button
          id={toggleId}
          variant={actionButtonVariant}
          size="sm"
          onClick={onAction}
          isLoading={isPending}
        >
          {actionLabel}
        </Button>
      );
    }
    // Default "toggle" variant.
    const { isChecked, onToggleChange } = props;
    return (
      <div className="Toggle__wrapper">
        {isPending ? <Loader size="1rem" /> : null}
        <Toggle
          id={toggleId}
          checked={isChecked}
          onChange={onToggleChange}
          disabled={isPending}
          fieldSize="sm"
        />
      </div>
    );
  };
  return (
    <>
      {error ? (
        <Notification variant="error" title="Error" isFilled={true}>
          <ErrorWithExtras appError={error} />
        </Notification>
      ) : null}
      <div className={`CardWithToggler__wrapper ${isHighlighted ? "is-highlighted" : ""}`}>
        <Card>
          <div className="CardStack__card">
            <div className="SdpSettings">
              <div className="SdpSettings__row">
                <div className="SdpSettings__item">
                  <label className="SdpSettings__label" htmlFor={toggleId}>
                    {label}
                  </label>
                  {renderControl()}
                </div>
                <div className="Note" style={{ width: "75%" }}>
                  {note}
                </div>
              </div>
              {children}
            </div>
          </div>
        </Card>
      </div>
    </>
  );
};
